import { useQuery } from '@tanstack/react-query';
import PluginService from 'services/PluginService';
import { PLUGINS } from 'queries/query-keys';
import {
  businessAppResumeQuery,
  RESUME_LOOKUP_OPTIONS,
  useResumeConversationId,
  type ResumeLookup,
} from 'ts-components/PiChat/kicodeSessions';
import type { KiCodeSessionState } from '../kiCodeState';

export const businessPluginAppId = (app: unknown): string | null => {
  const id = (app as { id?: unknown } | null | undefined)?.id;
  return typeof id === 'string' && id ? id : null;
};

/**
 * Only a fresh open of a pushed app looks a conversation up; a stored resume
 * or an explicit new chat decides alone.
 */
export const businessAppLookupName = ({
  target,
  newConversation,
  resumeId,
}: KiCodeSessionState): string | null =>
  target.context.appApiName !== undefined &&
  !newConversation &&
  resumeId === null
    ? target.context.appApiName
    : null;

export const businessAppIdQueryOptions = (
  apiName: string | null,
  employeeId: string | null | undefined
) => ({
  queryKey: PLUGINS.GET_BUSINESS_PLUGIN_APP(apiName),
  queryFn: async ({ signal }: { signal: AbortSignal }) =>
    apiName
      ? businessPluginAppId(
          await PluginService.getBusinessPluginApp(apiName, { signal })
        )
      : null,
  enabled: Boolean(apiName && employeeId),
  ...RESUME_LOOKUP_OPTIONS,
});

/** Pending until the app id and then its linked session have resolved. */
export const settleBusinessAppResume = (
  lookup: boolean,
  appPending: boolean,
  linked: ResumeLookup,
  marker: string | null
): ResumeLookup => {
  if (!lookup) return { pending: false, conversationId: marker };
  if (appPending) return { pending: true };
  return linked;
};

/**
 * Resolves the app's id, then its linked session. Without an app or employee
 * id `marker` decides now; a failed or empty lookup starts a new conversation.
 */
export const useBusinessAppResume = (
  appApiName: string | null,
  employeeId: string | null | undefined,
  marker: string | null
): ResumeLookup => {
  const lookup = Boolean(appApiName && employeeId);
  const app = useQuery(businessAppIdQueryOptions(appApiName, employeeId));
  const linked = useResumeConversationId(
    businessAppResumeQuery(app.data, employeeId),
    null
  );
  return settleBusinessAppResume(lookup, app.isPending, linked, marker);
};
